import React from 'react';
import UserLink from '@wix/da-shared-react/pkg/User/Link';
import { getDeviationUrl } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import {
  PapiComment,
  PapiDeviation,
  PapiForum,
  PapiGallection,
  PapiUser,
} from '@wix/da-types-papi';
import { Trans, useTranslation } from 'react-i18next';
import { CommentSubject, SubjectType } from '../types';
import { MyComment, Props as MyCommentProps } from './MyComment';
import { Url } from '@wix/da-url';
import s from './MyComment.scss';

export interface Props {
  subject: CommentSubject;
  comment: PapiComment;
  isOwner: boolean;
}
export type MyCommentPropsPartial = Without<MyCommentProps, 'title' | 'thumb'>;

export const MyCommentFactory: React.FC<Props & MyCommentPropsPartial> = ({
  subject,
  isOwner,
  ...otherProps
}) => {
  const { t } = useTranslation();
  // edge case: this had happened before
  if (!otherProps.comment) {
    return null;
  }

  let title, thumb; // pickup the bread crumb
  const commentedBy = isOwner ? (
    <span className={s['commented-by-you']}>{t('common.you')}</span>
  ) : (
    <UserLink
      className={s['userlink']}
      showSymbol={false}
      user={otherProps.comment.user}
      withTooltip={true}
    />
  );

  const subjectType = Object.values(SubjectType)
    .filter(key => subject.hasOwnProperty(key))
    .pop();

  switch (subjectType) {
    case SubjectType.DEVIATION:
      thumb = subject.deviation as PapiDeviation;
      if (!thumb) {
        return null;
      }
      title = thumb.status ? (
        <Trans i18nKey="widgets.myComments.status">
          {commentedBy}
          {' commented on '}
          <strong>
            <a href={getDeviationUrl(thumb)} title="status">
              {' a status '}
            </a>
          </strong>
          {' by '}
          <UserLink
            className={s['userlink']}
            user={thumb.author}
            showSymbol={false}
            withTooltip={true}
          />
        </Trans>
      ) : (
        <Trans i18nKey="widgets.myComments.deviation">
          {commentedBy}
          {' commented on '}
          <strong>
            <a href={getDeviationUrl(thumb)} title={thumb.title}>
              {thumb.title}
            </a>
          </strong>
          {' by '}
          <UserLink
            className={s['userlink']}
            user={thumb.author}
            showSymbol={false}
            withTooltip={true}
          />
        </Trans>
      );
      break;

    case SubjectType.COMMENT:
      const comment = subject.comment as PapiComment;
      if (!comment) {
        return null;
      }
      title = (
        <Trans i18nKey="widgets.myComments.comment">
          {commentedBy}
          {' commented on '}
          <UserLink
            className={s['userlink']}
            user={comment.user}
            showSymbol={false}
            withTooltip={true}
          />
          {"'s "}
          <strong>
            <a
              href={Url.commentLink(
                comment.typeId,
                comment.itemId,
                comment.commentId
              )}
            >
              comment
            </a>
          </strong>
        </Trans>
      );
      break;

    case SubjectType.FORUM:
      const forum = subject.forum as PapiForum;
      if (!forum) {
        return null;
      }
      title = (
        <Trans i18nKey="widgets.myComments.forum">
          {commentedBy}
          {' commented on '}
          <strong>
            <a href={Url.forumLink(forum.forumPath, forum.threadId)}>
              {forum.subject}
            </a>
          </strong>
          {' by '}
          <UserLink
            className={s['userlink']}
            user={forum.author}
            showSymbol={false}
            withTooltip={true}
          />
        </Trans>
      );
      break;

    case SubjectType.COLLECTION:
      const collection = subject.collection as PapiGallection;
      if (!collection) {
        return null;
      }
      title = (
        <Trans i18nKey="widgets.myComments.collection">
          {commentedBy}
          {' commented on '}
          <UserLink
            className={s['userlink']}
            user={collection.owner}
            showSymbol={false}
            withTooltip={true}
          />
          {"'s "}
          <strong>
            <a
              href={Url.userGalleryPath({
                username: collection.owner.username,
                gallectionType: collection.type as 'gallery' | 'collection',
                folderId: collection.folderId,
                folderName: collection.name,
              })}
            >
              collection
            </a>
          </strong>
        </Trans>
      );
      break;

    case SubjectType.PROFILE:
      const profile = subject.profile as PapiUser;
      if (!profile) {
        return null;
      }
      title = (
        <Trans i18nKey="widgets.myComments.profile">
          {commentedBy}
          {' commented on '}
          <UserLink
            className={s['userlink']}
            showSymbol={false}
            user={profile}
            withTooltip={true}
          />
          {"'s profile"}
        </Trans>
      );
      break;

    default:
      return null;
  }

  return <MyComment title={title} thumb={thumb} {...otherProps} />;
};

MyCommentFactory.displayName = 'MyCommentFactory';

export default MyCommentFactory;
